import {json} from "d3-fetch";
import {create, select, selectAll} from 'd3-selection';
import {chordDirected, ribbonArrow} from "d3-chord";
import {descending, sort, union} from "d3-array";
import {arc} from "d3-shape";
import {quantize} from "d3-interpolate";
import {interpolateRainbow} from "d3-scale-chromatic";
import {transition} from "d3-transition"; // ignore unused import error

const width =  829; // 1080;
const height = 794; // 1080;
const innerRadius = Math.min(width, height) * 0.5 - 90;
const outerRadius = innerRadius + 10;
const transitionDuration = 250;
let chordDiagramSVG,matrix,labelsG,arcsG,ribbonsG;
let totalRequests, totalDisclosures, disclosures, requests;

const renderStateExchange = async () => {
    if ($(".state-exchange-elementor-widget").length <= 0 ) {
        return;
    }
    await json(`/wp-json/data/state-exchange`)
        .then(async (res) => {
            let state;
            let {data, states} = res;
            ({totalRequests, totalDisclosures, disclosures, requests} = res);

            // state-list-control
            const stateListControl = select(".state-exchange-elementor-widget #state-list-control");
            stateListControl
                .selectAll("option.state")
                .data(states)
                .enter()
                .append("option")
                .attr("value", d => d.state)
                .text(d => d.name)
                .classed("state");

            await chordDiagramObj(data, state)
                .then(async ({data: filteredData, names, d3arc, ribbon, colors, chords}) => {
                    // Create the Chord Diagram SVG container
                    chordDiagramSVG = create("svg")
                        .attr("width", width)
                        .attr("height", height)
                        .attr("viewBox", [-width / 2, -height / 2, width, height])
                        .attr("style", `width: 100%; height: 100%; font: 10px sans-serif;`);

                    labelsG  = chordDiagramSVG.append("g").attr("id", "labels");
                    arcsG    = chordDiagramSVG.append("g").attr("id", "arcs");
                    ribbonsG = chordDiagramSVG.append("g").attr("id", "ribbons");

                    chordDiagramSVG = renderChordDiagram(filteredData, state, names, d3arc, ribbon, colors, chords);

                    if (chordDiagramSVG) {
                        select(".state-exchange-elementor-widget #chord-diagram")
                            .node()
                            .appendChild(chordDiagramSVG.node());
                    }

                    attachChordDiagramEventHandlers(data);
                    attachControlsEventHandlers(data);
                });
        })
        .catch(error => {
            console.error('error', error)
        });
}

const updateChordDiagramGraph = (data, state) => {
    if (chordDiagramSVG) {
        chordDiagramSVG.update(data, state);
    }
}

// Chord Diagram Event Listeners
const attachChordDiagramEventHandlers = (data) => {
    select("#state-list-control").on("change", function() {
        updateChordDiagramGraph(data, this.value === "null" ? null : this.value);
    });

    selectAll("text.titles").on("click", function() {
        const state = this.getAttribute("data-state");
        // Set the state list control to the selected state
        select("#state-list-control").node().value = state;
        updateChordDiagramGraph(data, state);
    });
}

const attachControlsEventHandlers = (data) => {
    selectAll(".about-this-diagram").on("click", function() {
        // Show the modal
        //select("#informative-overlay").style("display", "block");
        select("#informative-overlay").attr("class", "display");
    });

    select("#informative-overlay .close").on("click", function() {
        // Close the modal
        //select("#informative-overlay").style("display", "none");
        select("#informative-overlay").attr("class", "");
    });
}

// Render Chord Diagram
const filterData = async (data, state) => {
    // Filter data to only include the state of interest
    if (typeof state !== 'undefined' && state) {
        data = data.filter(d => d.requesting_state === state || d.disclosing_state === state);
    }

    // Filter data to remove self-links
    // data = data.filter(d => {
    //     return d.requesting_state !== d.disclosing_state
    // });

    // Object.Assign the values to the expected format
    data = data.map(d => {
        d.source = d.requesting_state;
        d.target = d.disclosing_state;
        d.value = d.requests;
        d.frequency = d.disclosures; //d.requests;
        d.letter = d.requesting_state; // d.disclosing_state;

        d.frequency_requests = d.requests;
        d.frequency_disclosures = d.disclosures;

        d.letter_requests = d.requesting_state;
        d.letter_disclosures = d.requesting_state;
        return d;
    });

    return new Promise(
        resolve => resolve(data)
    );
}

const chordGenerator = (data, names) => {
    // Compute a dense matrix from the weighted links in data.
    const index = new Map(names.map((name, i) => [name, i]));
    matrix = Array.from(index, () => new Array(names.length).fill(0));

    for (const {source, target, value} of data) {
        matrix[index.get(source)][index.get(target)] = +value;
    }

    const chord = chordDirected()
        .padAngle(10 / innerRadius)
        .sortSubgroups(descending)
        .sortChords(descending);

    return chord(matrix);
}

const chordDiagramObj = async (data, state) => {
    await filterData(data, state).then(d => data = d);
    const names = sort(union(data.map(d => d.source), data.map(d => d.target)));
    const d3arc = arc()
        .innerRadius(innerRadius)
        .outerRadius(outerRadius);
    const ribbon = ribbonArrow()
        .radius(innerRadius - 1)
        .padAngle(1 / innerRadius);
    const colors = quantize(interpolateRainbow, names.length);
    const chords = chordGenerator(data, names);

    return new Promise(
        resolve => resolve({data, names, d3arc, ribbon, colors, chords})
    );
}

function adjustDataExchangesColumns(topList) {
    let mobile = "grid-template-columns: 80px 11rem;"

    // If the screen width is above 1200px then adjust the grid columns
    let columns;
    // Find if any of the values is greater or equals than 100 billion
    if (topList.some(d => d.value >= 100000000000)) {
        columns = "19rem";
    } else if (topList.some(d => d.value >= 100000000)) {
        columns = "15rem";
    } else if (topList.some(d => d.value >= 10000000)) {
        columns = "14rem";
    } else if (topList.some(d => d.value >= 1000000)) {
        columns = "12rem";
    } else if (topList.some(d => d.value >= 10000)) {
        columns = "8rem";
    } else {
        columns = "5rem";
    }

    let desktop = `grid-template-columns: 115px ${columns}`;

    return {
        value: window.innerWidth >= 1200 ? desktop : mobile,
        mobile: mobile,
        desktop: desktop
    }
}

const renderChordDiagram = (data, state, names, d3arc, ribbon, colors, chords) => {
    /** Labels **/
    const labelsUpdate = labelsG
        .selectAll("text.titles")
        .data(chords.groups);

    const labelsEnter = labelsUpdate
        .enter();

    labelsUpdate
        .merge(labelsEnter)
        .append("text")
        .attr("class", "titles")
        .attr("opacity", 0)
        .transition()
        .duration(transitionDuration)
        .each(d => (d.angle = (d.startAngle + d.endAngle) / 2))
        .attr("dy", "0.35em")
        .attr("text-anchor", function(d) { return d.angle > Math.PI ? "end" : null; })
        .attr("transform", d =>
            `rotate(${(d.angle * 180 / Math.PI - 90)})
             translate(${outerRadius + 5})
             ${d.angle > Math.PI ? "rotate(180)" : ""}`
        )
        .attr("data-state", d => names[d.index])
        .text(d => names[d.index])
        .attr("opacity", 1);

    labelsUpdate
        .exit()
        .remove();

    /** Arcs **/
    const arcsUpdate = arcsG
        .selectAll("path.arc")
        .data(chords.groups)

    const arcsEnter = arcsUpdate
        .enter()
        .append("path")

    arcsUpdate
        .merge(arcsEnter)
        .attr("opacity", 0)
        .attr("class", "arc")
        .transition()
        .duration(transitionDuration)
        .attr("d", d3arc)
        .attr("fill", d => colors[d.index])
        .attr('opacity', 1);

    arcsUpdate
        .exit()
        .transition()
        .duration(transitionDuration)
        .attr("opacity", 0)
        .remove();

    /** Ribbons **/
    const ribbonsUpdate = ribbonsG
        .selectAll("path.ribbon")
        .data(chords, ({ source, target }) => source.index + '-' + target.index);

    const ribbonsEnter = ribbonsUpdate
        .enter()
        .append("path")
        .attr("class", "ribbon")
        .style("mix-blend-mode", "multiply");

    const getChordShare = (d, chords) => {
        let sourceValue = matrix[d.source.index][d.target.index];
        let targetValue = matrix[d.target.index][d.source.index];
        return `${sourceValue.toLocaleString("en")} ${names[d.source.index]} → ${names[d.target.index]}
            ${d.source.index === d.target.index ? ""
            : `\n${targetValue.toLocaleString("en")} ${names[d.target.index]} → ${names[d.source.index]}`}`;
    }

    ribbonsUpdate
        .merge(ribbonsEnter)
        .attr("opacity", 0)
        .transition()
        .duration(transitionDuration)
        .attr("d", ribbon)
        .attr("fill", d => colors[d.target.index])
        .attr("data-source", d => names[d.source.index])
        .attr("data-target", d => names[d.target.index])
        .attr("data-outgoing", d => {
            // Read the total requests from the Matrix
            // totalRequests += matrix[d.source.index][d.target.index];
            return matrix[d.source.index][d.target.index]; })
        .attr("data-incoming", d => {
            // Read the total disclosures from the Matrix
            // totalDisclosures += matrix[d.target.index][d.source.index];
            return matrix[d.target.index][d.source.index];
        })
        .attr('opacity', 1);

    ribbonsUpdate
        .merge(ribbonsEnter)
        .append("title")
        .data(chords, ({ source, target }) => source.index + '-' + target.index)
        .text(d => getChordShare(d, chords));

    ribbonsUpdate
        .exit()
        .transition()
        .duration(transitionDuration)
        .attr("opacity", 0)
        .remove();

    let totalRequestsVal = 0;
    let totalDisclosuresVal = 0;
    // If the selected state is null, then use the total requests and disclosures
    if (!state) {
        totalRequestsVal = totalRequests;
        totalDisclosuresVal = totalDisclosures;
    } else {
        // Filter the state from the requests array
        let request = requests.filter(d => d._id === state);
        let disclosure = disclosures.filter(d => d._id === state);
        if (request.length > 0) {
            totalRequestsVal = request[0].total;
        }
        if (disclosure.length > 0) {
            totalDisclosuresVal = disclosure[0].total;
        }
    }

    select("#total-requests").text(totalRequestsVal.toLocaleString("en"));
    select("#total-disclosures").text(totalDisclosuresVal.toLocaleString("en"));
    select("#sharing-percent").text(
        `${((totalRequestsVal > 0 ? totalDisclosuresVal / totalRequestsVal : 0) * 100).toFixed(2)}%`);

    select("ul#top-list")
        .selectAll("li")
        .remove();

    // List the top 3 states with the most shared data
    let topList = [];
    for (let i = 0; i < matrix.length; i++) {
        for (let j = 0; j < matrix[i].length; j++) {
            // Edge Case: State that only shares data with itself
            if (i === j && matrix.length === 1) {
                topList.push({source: names[i], target: names[j], value: matrix[i][j]});
            } else if (i !== j) {
                // Skip self-links
                topList.push({source: names[i], target: names[j], value: matrix[i][j]});
            }
        }
    }
    topList = topList.sort((a, b) => b.value - a.value).slice(0, 3);
    let topListStyle = adjustDataExchangesColumns(topList);

    topList.forEach(d => {
        select("ul#top-list")
            .append("li")
            .html(`<div class="wrap" style="${topListStyle.value}" data-desktop="${topListStyle.desktop}" data-mobile="${topListStyle.mobile}">
                    ${d.source} to ${d.target} <div class="value" style="">${d.value.toLocaleString("en")}</div></div>`);
    });

    if (topList.length < 3) {
        // Fill the rest of the list with N/A
        for (let i = topList.length; i < 3; i++) {
            select("ul#top-list")
                .append("li")
                .html(`<div class="wrap" style="${topListStyle.value}" data-desktop="${topListStyle.desktop}" data-mobile="${topListStyle.mobile}">
                    N/A</div>`);
        }
    }

    return Object.assign(
        chordDiagramSVG,
        {
            async update(data, state) {
                // Remove all the nodes and re-render
                select("#labels").selectAll("*").remove();
                select("#arcs").selectAll("*").remove();
                select("#ribbons").selectAll("*").remove();

                let filteredData, names, d3arc, ribbon, colors, chords;
                await chordDiagramObj(data, state)
                    .then(r => {
                            filteredData = r.data;
                            names = r.names;
                            d3arc = r.d3arc;
                            ribbon = r.ribbon;
                            colors = r.colors;
                            chords = r.chords;
                        }
                    );
                renderChordDiagram(filteredData, state, names, d3arc, ribbon, colors, chords);
                attachChordDiagramEventHandlers(data);
            }
        });
}


jQuery(document).ready(function () {
    renderStateExchange();
});

// Window resize listener
window.addEventListener("resize", () => {
    // For each ul#top-list li element, adjust the grid-template-columns
    document.querySelectorAll("ul#top-list .wrap").forEach((el) => {
        el.style = window.innerWidth >= 1200 ? el.dataset["desktop"] : el.dataset["mobile"];
    });
});
